import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import LazyImage from "./LazyImage";

import img01 from "@/assets/images/gallery-01.webp";
import img02 from "@/assets/images/gallery-02.webp";
import img03 from "@/assets/images/gallery-03.webp";
import img04 from "@/assets/images/gallery-04.webp";
import img05 from "@/assets/images/gallery-05.webp";
import img06 from "@/assets/images/gallery-06.webp";
import img07 from "@/assets/images/gallery-07.webp";
import img08 from "@/assets/images/gallery-08.webp";
import img09 from "@/assets/images/gallery-09.webp";
import img10 from "@/assets/images/gallery-10.webp";
import img11 from "@/assets/images/gallery-11.webp";
import img12 from "@/assets/images/gallery-12.webp";
import img13 from "@/assets/images/gallery-13.webp";

const images = [
  { src: img01, label: "01 · Начало" },
  { src: img02, label: "02 · Сияние свечей" },
  { src: img03, label: "03 · Чарующие звуки" },
  { src: img04, label: "04 · Признание" },
  { src: img05, label: "05 · Парижский салон" },
  { src: img06, label: "06 · Атмосфера" },
  { src: img07, label: "07 · Проживание" },
  { src: img08, label: "08 · Письма" },
  { src: img09, label: "09 · Зал и зрители" },
  { src: img10, label: "10 · Образы прошлого" },
  { src: img11, label: "11 · Погружение" },
  { src: img12, label: "12 · Встреча" },
  { src: img13, label: "13 · Любовь" },
];

export default function Gallery() {
  // 3× duplicate for smooth loop
  const repeated = [...images, ...images, ...images];

  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const setWidthRef = useRef(0); // width of ONE full set
  const lastTsRef = useRef(0);
  const rafRef = useRef(0);
  const draggingRef = useRef(false);
  const dragStart = useRef({ x: 0, offset: 0, moved: 0 });
  const speedRef = useRef(70);
  const [isDragging, setIsDragging] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(0);

  const normalize = (x) => {
    const w = setWidthRef.current;
    if (w <= 0) return x;
    while (x <= -w) x += w;
    while (x > 0) x -= w;
    return x;
  };

  const tick = useCallback((ts) => {
    const el = trackRef.current;
    if (!el) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    if (!lastTsRef.current) lastTsRef.current = ts;
    const dt = Math.min((ts - lastTsRef.current) / 1000, 0.05);
    lastTsRef.current = ts;
    if (!draggingRef.current && lightboxIdx === null && setWidthRef.current > 0) {
      offsetRef.current = normalize(offsetRef.current - speedRef.current * dt);
      el.style.transform = `translate3d(${offsetRef.current.toFixed(2)}px,0,0)`;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [lightboxIdx]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const measure = () => {
      setWidthRef.current = el.scrollWidth / 3;
    };
    const updateSpeed = () => {
      speedRef.current = window.innerWidth < 768 ? 50 : 70;
    };

    measure();
    updateSpeed();
    window.addEventListener("resize", measure);
    window.addEventListener("resize", updateSpeed);

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", measure);
      window.removeEventListener("resize", updateSpeed);
    };
  }, [tick]);

  // Re-measure once images finish loading (prevents flicker)
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    if (imagesLoaded >= images.length) {
      // small delay to let layout settle
      requestAnimationFrame(() => {
        setWidthRef.current = el.scrollWidth / 3;
      });
    }
  }, [imagesLoaded]);

  const onPointerDown = (e) => {
    const x = e.touches?.[0]?.clientX ?? e.clientX ?? 0;
    draggingRef.current = true;
    dragStart.current = { x, offset: offsetRef.current, moved: 0 };
    setIsDragging(true);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const x = e.touches?.[0]?.clientX ?? e.clientX ?? 0;
    const dx = x - dragStart.current.x;
    dragStart.current.moved = Math.abs(dx);
    offsetRef.current = normalize(dragStart.current.offset + dx);
    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${offsetRef.current.toFixed(2)}px,0,0)`;
    }
  };
  const onPointerUp = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setIsDragging(false);
    lastTsRef.current = 0;
  };

  useEffect(() => {
    const up = () => onPointerUp();
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    window.addEventListener("touchcancel", up);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
      window.removeEventListener("touchcancel", up);
    };
  }, []);

  const openLightbox = (i) => {
    if (dragStart.current.moved > 8) return;
    setLightboxIdx(i % images.length);
  };

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowRight") setLightboxIdx((v) => (v + 1) % images.length);
      if (e.key === "ArrowLeft") setLightboxIdx((v) => (v - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx]);

  return (
    <section
      id="galeria"
      className="relative pt-4 pb-12 lg:pt-6 lg:pb-16"
      data-testid="gallery-section"
    >
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-8 flex flex-col"
        >
          <div className="eyebrow mb-6">· Галерея</div>
          <h2
            className="font-serif font-light leading-[1] tracking-tight"
            style={{ fontSize: "clamp(2.4rem, 5vw, 4.2rem)", color: "#F2E9DE" }}
            data-testid="gallery-title"
          >
            Живые <em style={{ color: "#E57324" }}>эмоции</em>
          </h2>
        </motion.div>
      </div>

      <div
        className={`relative overflow-hidden select-none ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onTouchStart={onPointerDown}
        onTouchMove={onPointerMove}
        data-testid="gallery-track-wrap"
      >
        <div
          className="absolute inset-y-0 left-0 w-16 md:w-24 z-10 pointer-events-none"
          style={{ background: "linear-gradient(90deg, #080605 0%, rgba(8,6,5,0) 100%)" }}
        />
        <div
          className="absolute inset-y-0 right-0 w-16 md:w-24 z-10 pointer-events-none"
          style={{ background: "linear-gradient(270deg, #080605 0%, rgba(8,6,5,0) 100%)" }}
        />
        <div
          ref={trackRef}
          className="flex gap-4 px-4"
          style={{
            transform: "translate3d(0,0,0)",
            willChange: "transform",
            backfaceVisibility: "hidden",
          }}
          data-testid="gallery-track"
        >
          {repeated.map((img, i) => (
            <div
              key={i}
              onClick={() => openLightbox(i)}
              data-testid={`gallery-item-${i}`}
              className="relative flex-none w-[78vw] sm:w-[420px] lg:w-[520px] aspect-square overflow-hidden group"
            >
              {/* LazyImage con blur-up placeholder y aspect-ratio cuadrado */}
              <LazyImage
                src={img.src}
                alt={img.label}
                aspectRatio="1 / 1"
                containerClassName="absolute inset-0 w-full h-full"
                className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-105 pointer-events-none"
                objectFit="cover"
                onLoad={() => setImagesLoaded((v) => v + 1)}
              />
              <div
                className="absolute inset-0 transition-opacity duration-500 group-hover:opacity-30 pointer-events-none z-[2]"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(8,6,5,0.05) 0%, rgba(8,6,5,0.65) 100%)",
                }}
              />
              <div className="absolute bottom-5 left-5 right-5 z-10 text-left pointer-events-none">
                <div className="text-[0.65rem] tracking-[0.3em] uppercase text-[#E57324] mb-1">
                  Сцена
                </div>
                <div className="font-serif text-xl text-[#F2E9DE]">
                  {img.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
            style={{ background: "rgba(5,3,3,0.95)", backdropFilter: "blur(10px)" }}
            onClick={() => setLightboxIdx(null)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
              className="absolute top-6 right-6 text-[#F2E9DE] hover:text-[#E57324] z-10"
              data-testid="gallery-lightbox-close"
              aria-label="close"
            >
              <X size={28} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIdx((v) => (v - 1 + images.length) % images.length); }}
              className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 text-[#F2E9DE] hover:text-[#E57324] z-10"
              data-testid="gallery-lightbox-prev"
              aria-label="prev"
            >
              <ChevronLeft size={36} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIdx((v) => (v + 1) % images.length); }}
              className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 text-[#F2E9DE] hover:text-[#E57324] z-10"
              data-testid="gallery-lightbox-next"
              aria-label="next"
            >
              <ChevronRight size={36} />
            </button>
            <motion.div
              key={lightboxIdx}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="relative max-w-5xl max-h-[85vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[lightboxIdx].src}
                alt={images[lightboxIdx].label}
                className="w-full h-full object-contain max-h-[85vh]"
                draggable={false}
              />
              <div className="text-center mt-4 text-sm tracking-[0.2em] uppercase text-[#F2E9DE]">
                {images[lightboxIdx].label}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
